/**
 * Nano Banana 3.1 图片生成（通过后端服务器中转）
 *
 * 后端接口：
 *   v2.5: POST /web/v1/nanoBanana/v2-5/createImage — 固定 1024px
 *   v3.1: POST /web/v1/nanoBanana/v3-1/createImage — 支持 512px/1K/2K/4K
 *
 * 后端返回格式（同步）：
 *   {
 *     "code": 0,
 *     "message": "success",
 *     "data": {
 *       "status": "succeed",
 *       "imageData": "iVBORw0KGgo...",   // base64 图片数据
 *       "mimeType": "image/png",          // 图片类型
 *       "text": "Here is the image..."    // 模型返回的文本（可能没有）
 *     }
 *   }
 *
 * 处理流程：base64 → File → uploadFile → URL
 */
import apiClient from '../../../config'
import { uploadFile } from '../../../upload'

/** 后端接口路径 */
var CREATE_PATH_V25 = '/nanoBanana/v2-5/createImage'
var CREATE_PATH_V31 = '/nanoBanana/v3-1/createImage'

/**
 * 获取用户登录凭证
 */
function getMiToken() {
  return localStorage.getItem('token') || ''
}

/**
 * base64 字符串解码为 File 对象
 */
function decodeBase64ToFile(base64Data, mimeType, baseName) {
  var binStr = atob(base64Data)
  var len = binStr.length
  var bytes = new Uint8Array(len)
  var i = 0
  for (i = 0; i < len; i++) {
    bytes[i] = binStr.charCodeAt(i)
  }
  var mt = String(mimeType || 'image/png')
  var ext = mt.indexOf('jpeg') !== -1 ? 'jpg' : mt.indexOf('webp') !== -1 ? 'webp' : 'png'
  var fileName = String(baseName || 'nano-banana') + '.' + ext
  return new File([bytes], fileName, { type: mt })
}

/**
 * 解析后端返回的 base64 图片并上传为文件 URL
 * @param {Object} resData - 后端 data 字段
 * @param {string} baseName - 文件名前缀
 * @returns {Promise<string[]>} 图片 URL 数组
 */
function handleBase64Response(resData, baseName) {
  if (!resData) {
    return Promise.reject(new Error('后端接口返回数据为空'))
  }
  var imageData = resData.imageData
  if (!imageData || typeof imageData !== 'string' || !imageData.trim()) {
    return Promise.reject(new Error('后端接口未返回图片数据'))
  }
  var mimeType = resData.mimeType || 'image/png'
  var file = decodeBase64ToFile(imageData, mimeType, baseName)
  return uploadFile(file).then(function (url) {
    return [url]
  })
}

/**
 * 构建通用请求参数
 */
function buildRequest(params) {
  var token = getMiToken()
  var req = {
    miToken: token,
    prompt: (params.prompt || '').trim().slice(0, 2000)
  }

  if (params.referenceImage && typeof params.referenceImage === 'string' && params.referenceImage.trim().length > 0) {
    req.referenceImage = params.referenceImage.trim()
  }

  if (params.aspectRatio) {
    req.aspectRatio = String(params.aspectRatio).trim()
  }

  return req
}

/**
 * 提交 Nano Banana v2.5 图片生成（后端中转，同步返回 base64）
 * v2.5 固定 1024px，不支持 imageSize 参数
 * @param {Object} params
 * @returns {Promise<{urls: string[], provider: string, sync: boolean}>}
 */
export function submitNanoBananaV25Image(params) {
  var req = buildRequest(params)
  return apiClient.post(CREATE_PATH_V25, req).then(function (data) {
    var resData = data && data.data
    return handleBase64Response(resData, 'nano-banana-v25-' + Date.now())
  }).then(function (urls) {
    return { urls: urls, provider: 'official', sync: true }
  })
}

/**
 * 提交 Nano Banana v3.1 图片生成（后端中转，同步返回 base64）
 * v3.1 支持 imageSize 参数："512px","1K","2K","4K"
 * @param {Object} params
 * @returns {Promise<{urls: string[], provider: string, sync: boolean}>}
 */
export function submitNanoBananaV31Image(params) {
  var req = buildRequest(params)

  var imageSize = String(params.imageSize || '1K').trim()
  if (imageSize === '1K' || imageSize === '2K' || imageSize === '4K') {
    req.imageSize = imageSize
  }

  return apiClient.post(CREATE_PATH_V31, req).then(function (data) {
    var resData = data && data.data
    return handleBase64Response(resData, 'nano-banana-v31-' + Date.now())
  }).then(function (urls) {
    return { urls: urls, provider: 'official', sync: true }
  })
}
